import { Button, Divider, Grid, IconButton, TextField } from '@mui/material'
import { PiPlusCircleLight } from 'react-icons/pi'
import { MdDeleteOutline } from 'react-icons/md'

const isValidDate = (date) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date || '')) return false

  const [year, month, day] = date.split('-').map(Number)
  const parsedDate = new Date(Date.UTC(year, month - 1, day))

  return (
    parsedDate.getUTCFullYear() === year &&
    parsedDate.getUTCMonth() === month - 1 &&
    parsedDate.getUTCDate() === day
  )
}

export const validateWeaponDocuments = (documents) => {
  const errors = {}
  const selectedDocumentTypes = new Set()

  documents.forEach((document, index) => {
    const rowErrors = {}
    const documentTypeID = String(document.controleDocumentoID || '')

    if (!documentTypeID) {
      rowErrors.controleDocumentoID = 'Selecione um documento.'
    } else if (selectedDocumentTypes.has(documentTypeID)) {
      rowErrors.controleDocumentoID = 'Este documento já foi adicionado.'
    } else {
      selectedDocumentTypes.add(documentTypeID)
    }

    if (!document.dataInicio) {
      rowErrors.dataInicio = 'Informe a data de emissão.'
    } else if (!isValidDate(document.dataInicio)) {
      rowErrors.dataInicio = 'Informe uma data de emissão válida.'
    }

    if (!document.dataVencimento) {
      rowErrors.dataVencimento = 'Informe a data de vencimento.'
    } else if (!isValidDate(document.dataVencimento)) {
      rowErrors.dataVencimento = 'Informe uma data de vencimento válida.'
    } else if (
      isValidDate(document.dataInicio) &&
      document.dataVencimento < document.dataInicio
    ) {
      rowErrors.dataVencimento = 'O vencimento não pode ser anterior à emissão.'
    }

    if (Object.keys(rowErrors).length > 0) {
      errors[index] = rowErrors
    }
  })

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

const isCompleteDocument = (document) =>
  Boolean(
    document.controleDocumentoID &&
    document.dataInicio &&
    document.dataVencimento
  )

const WeaponDocumentsEditor = ({
  enabled,
  documentTypes = [],
  documents = [],
  onChange,
  errors = {},
}) => {
  if (!enabled) return null

  const selectedDocumentTypes = new Set(
    documents
      .map((document) => String(document.controleDocumentoID || ''))
      .filter(Boolean)
  )
  const hasIncompleteDocument = documents.some(
    (document) => !isCompleteDocument(document)
  )
  const canAddDocument =
    !hasIncompleteDocument &&
    documentTypes.length > 0 &&
    selectedDocumentTypes.size < documentTypes.length

  const updateDocument = (index, field, value) => {
    const updatedDocuments = documents.map((document, documentIndex) => {
      if (documentIndex !== index) return document

      if (field === 'controleDocumentoID') {
        const selectedType = documentTypes.find(
          (documentType) => String(documentType.id) === String(value)
        )

        return {
          ...document,
          controleDocumentoID: value,
          nome: selectedType?.nome || '',
        }
      }

      return { ...document, [field]: value }
    })

    onChange(updatedDocuments)
  }

  const addDocument = () => {
    if (!canAddDocument) return

    onChange([
      ...documents,
      {
        clientID: `new-${Date.now()}-${documents.length}`,
        controleDocumentoID: '',
        dataInicio: '',
        dataVencimento: '',
      },
    ])
  }

  const removeDocument = (index) => {
    onChange(documents.filter((_, documentIndex) => documentIndex !== index))
  }

  return (
    <Grid item xs={12}>
      <Divider className="!my-3" />
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="font-semibold">Documentos da arma</h2>
          <p className="text-sm opacity-70">
            Informe o documento e suas datas de emissão e vencimento.
          </p>
        </div>

        {documents.length === 0 ? (
          <div className="rounded-lg border border-dashed border-black/20 p-4 text-center text-sm opacity-70">
            Nenhum documento cadastrado.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {documents.map((document, index) => {
              const rowErrors = errors[index] || {}
              const selectedDocumentTypeID = String(
                document.controleDocumentoID || ''
              )

              return (
                <div
                  key={
                    document.pessoaArmaControleDocumentoID ||
                    document.clientID ||
                    index
                  }
                  data-testid="weapon-document-row"
                  className="rounded-lg border border-black/10 p-3"
                >
                  <Grid container spacing={4} alignItems="flex-start">
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        SelectProps={{ native: true }}
                        fullWidth
                        size="small"
                        label={`Documento ${index + 1}`}
                        value={selectedDocumentTypeID}
                        onChange={(event) =>
                          updateDocument(
                            index,
                            'controleDocumentoID',
                            event.target.value
                          )
                        }
                        error={Boolean(rowErrors.controleDocumentoID)}
                        helperText={rowErrors.controleDocumentoID}
                      >
                        <option value="">Selecione</option>
                        {documentTypes.map((documentType) => {
                          const documentTypeID = String(documentType.id)
                          const usedByAnotherRow =
                            documentTypeID !== selectedDocumentTypeID &&
                            selectedDocumentTypes.has(documentTypeID)

                          return (
                            <option
                              key={documentType.id}
                              value={documentTypeID}
                              disabled={usedByAnotherRow}
                            >
                              {documentType.nome}
                            </option>
                          )
                        })}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={5} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label={`Data de emissão ${index + 1}`}
                        value={document.dataInicio || ''}
                        onChange={(event) =>
                          updateDocument(
                            index,
                            'dataInicio',
                            event.target.value
                          )
                        }
                        error={Boolean(rowErrors.dataInicio)}
                        helperText={rowErrors.dataInicio}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={10} sm={5} md={3}>
                      <TextField
                        fullWidth
                        size="small"
                        type="date"
                        label={`Data de vencimento ${index + 1}`}
                        value={document.dataVencimento || ''}
                        onChange={(event) =>
                          updateDocument(
                            index,
                            'dataVencimento',
                            event.target.value
                          )
                        }
                        error={Boolean(rowErrors.dataVencimento)}
                        helperText={rowErrors.dataVencimento}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={2} md={2} className="flex justify-end">
                      <IconButton
                        color="error"
                        aria-label={`Remover documento ${index + 1}`}
                        onClick={() => removeDocument(index)}
                      >
                        <MdDeleteOutline />
                      </IconButton>
                    </Grid>
                  </Grid>
                </div>
              )
            })}
          </div>
        )}

        <Button
          fullWidth
          variant="outlined"
          color="primary"
          startIcon={<PiPlusCircleLight />}
          onClick={addDocument}
          disabled={!canAddDocument}
        >
          Adicionar documento
        </Button>
      </div>
    </Grid>
  )
}

export default WeaponDocumentsEditor
